import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, BookOpen, CheckCircle2, Copy, Eye, EyeOff, KeyRound, LogIn, Mail, RefreshCw, Terminal, UserPlus, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TERMS_VERSION, legalDocuments } from "@/legal/legalContent";

function normalizeAccessKeyForLogin(value) {
  const cleaned = String(value || "").normalize("NFKC").trim().toUpperCase()
    .replace(/[‐‑‒–—−]/g, "-")
    .replace(/[\s\u200B-\u200D\u2060\uFEFF]+/g, "");
  const compact = cleaned.replace(/[^A-Z0-9]/g, "");
  const marker = compact.lastIndexOf("RAAVA");
  const body = marker >= 0 ? compact.slice(marker + 5) : compact;
  if (body.length === 8) return `RAAVA-${body.slice(0, 4)}-${body.slice(4, 8)}`;
  if (body.length === 16) return `RAAVA-${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8, 12)}-${body.slice(12, 16)}`;
  return cleaned;
}

function TermsReviewModal({ open, reachedEnd, onReachedEnd, onAccept, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 px-4 py-6 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="terms-review-title">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[26px] border border-[#35AFC0]/35 bg-[#08080D] shadow-[0_40px_140px_rgba(0,0,0,0.8)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#26262E] px-5 py-5 sm:px-7">
          <div>
            <p className="mb-2 text-[9px] uppercase tracking-[0.3em] text-[#4FAEBB]">Required before registration</p>
            <h3 id="terms-review-title" className="font-display text-2xl text-white" style={{ fontWeight: 700 }}>Raava Terms of Use</h3>
            <p className="mt-2 text-xs leading-6 text-[#8E8E99]">Read the complete document and scroll to the end to unlock acceptance.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-[#2A2A32] p-2.5 text-[#898994] hover:border-[#FF3366]/45 hover:text-white" aria-label="Close Terms">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-5 py-6 sm:px-7"
          data-lenis-prevent
          onWheel={(event) => event.stopPropagation()}
          onTouchMove={(event) => event.stopPropagation()}
          onScroll={(event) => {
            const node = event.currentTarget;
            if (node.scrollHeight - node.scrollTop - node.clientHeight <= 24) onReachedEnd();
          }}
          tabIndex={0}
          data-testid="terms-scroll-container"
        >
          <div className="space-y-7 pr-2">
            {legalDocuments.terms.sections.map((section) => (
              <section key={section.title}>
                <h4 className="mb-3 text-sm font-semibold text-[#EDEDF1]">{section.title}</h4>
                {section.paragraphs?.map((paragraph) => <p key={paragraph} className="mt-2 text-xs leading-6 text-[#A0A0AB] first:mt-0">{paragraph}</p>)}
                {section.bullets && (
                  <ul className="mt-3 space-y-2">
                    {section.bullets.map((bullet) => <li key={bullet} className="flex gap-3 text-xs leading-6 text-[#A0A0AB]"><span className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-[#35AFC0]" />{bullet}</li>)}
                  </ul>
                )}
              </section>
            ))}
            <div className="rounded-xl border border-[#2B9FB4]/25 bg-[#2B9FB4]/[0.05] p-4 text-xs leading-6 text-[#8FCBD3]">
              End of Terms · Version {TERMS_VERSION}
            </div>
          </div>
        </div>

        <div className="border-t border-[#26262E] bg-[#060609] px-5 py-5 sm:px-7">
          <div className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]">
            {reachedEnd ? <CheckCircle2 className="h-4 w-4 text-[#35C982]" /> : <BookOpen className="h-4 w-4 text-[#7D7D89]" />}
            <span className={reachedEnd ? "text-[#65DDA1]" : "text-[#7D7D89]"}>{reachedEnd ? "Complete document read" : "Scroll to the end to continue"}</span>
          </div>
          <button
            type="button"
            disabled={!reachedEnd}
            onClick={onAccept}
            className="w-full rounded-xl border border-[#388FA4]/55 bg-gradient-to-r from-[#0E5261] via-[#145A70] to-[#303F82] py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#F3FCFF] transition hover:brightness-115 disabled:cursor-not-allowed disabled:opacity-25"
            data-testid="accept-terms-button"
          >
            I have read and accept the Terms
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AccessSection() {
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginKind, setLoginKind] = useState("email");
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [issuedKey, setIssuedKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [verificationEmail, setVerificationEmail] = useState("");
  const [resendBusy, setResendBusy] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsReachedEnd, setTermsReachedEnd] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const selectAccessMode = (event) => {
      if (event.detail !== "login" && event.detail !== "register") return;
      setMode(event.detail);
      setIssuedKey(null);
    };

    window.addEventListener("raava:access-mode", selectAccessMode);
    return () => window.removeEventListener("raava:access-mode", selectAccessMode);
  }, []);

  useEffect(() => {
    const requestedMode = new URLSearchParams(location.search).get("auth");
    if (requestedMode === "login" || requestedMode === "register" || requestedMode === "forgot") {
      setMode(requestedMode);
      setIssuedKey(null);
      if (requestedMode === "forgot" && identifier.includes("@")) setRecoveryEmail(identifier);
    }
  }, [identifier, location.search]);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => setResendCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!termsAccepted) {
      setTermsOpen(true);
      toast.error("Read and accept the Terms before creating an account");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("The passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const data = await register(name, email, password, TERMS_VERSION);
      setIssuedKey(data.access_key);
      toast.success("Key generated — please verify your email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    // Keep Access Keys in React state instead of reading a password-manager
    // field. Some browsers replace or truncate custom password values during
    // form serialization/autofill without dispatching a matching input event.
    const rawIdentifier = String(loginKind === "key" ? identifier : form.get("username") || "");
    const submittedIdentifier = loginKind === "key" ? normalizeAccessKeyForLogin(rawIdentifier) : rawIdentifier.trim();
    const submittedPassword = loginKind === "key" ? "" : String(form.get("password") || "");
    if (!submittedIdentifier || (loginKind === "email" && !submittedPassword)) {
      toast.error(loginKind === "key" ? "Enter your Access Key" : "Enter your email and password");
      return;
    }
    setBusy(true);
    setVerificationEmail("");
    try {
      await login(submittedIdentifier, submittedPassword, loginKind);
      toast.success("Signed in. Welcome back.");
      const requested = typeof location.state?.returnTo === "string" ? location.state.returnTo : "/profile";
      const returnTo = requested.startsWith("/") && !requested.startsWith("//") ? requested : "/profile";
      navigate(returnTo, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || "Login failed";
      if (err.response?.status === 403 && detail.toLowerCase().includes("not been verified")) {
        setVerificationEmail(submittedIdentifier);
      }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const openPasswordRecovery = () => {
    setRecoveryEmail(identifier.includes("@") ? identifier.trim() : "");
    setRecoveryMessage("");
    setMode("forgot");
  };

  const handlePasswordResetRequest = async (event) => {
    event.preventDefault();
    setBusy(true);
    setRecoveryMessage("");
    try {
      const response = await api.post("/auth/password-reset/request", { email: recoveryEmail.trim() });
      setRecoveryMessage(response.data.message || "If a matching account exists, an email has been sent.");
      toast.success("Check your inbox for the reset link");
    } catch (error) {
      toast.error(error.response?.data?.detail || "The reset email could not be requested");
    } finally {
      setBusy(false);
    }
  };

  const resendVerification = async (targetEmail) => {
    const address = String(targetEmail || "").trim();
    if (!address || resendBusy || resendCooldown > 0) return;
    setResendBusy(true);
    try {
      const response = await api.post("/auth/verification/resend", { email: address });
      toast.success(response.data.message || "If verification is still required, a new email has been sent.");
      setResendCooldown(30);
    } catch (error) {
      toast.error(error.response?.data?.detail || "The verification email could not be sent");
    } finally {
      setResendBusy(false);
    }
  };

  const copyKey = () => {
    if (issuedKey) {
      navigator.clipboard.writeText(issuedKey);
      toast.success("Key copied");
    }
  };

  return (
    <section id="access" className="relative px-6 sm:px-12 lg:px-24 py-32 lg:py-40 border-t border-[#222222] overflow-hidden" data-testid="access-section">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_48%,rgba(43,159,180,0.055),transparent_38%)]" />
      <div className="relative max-w-5xl mx-auto">
        <p className="text-xs uppercase tracking-[0.4em] text-[#35AFC0] mb-6 text-center">// Access Terminal</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-center text-[#F4F4F5] mb-5" style={{ fontWeight: 700 }}>
          One login. Every Raava project.
        </h2>
        <p className="max-w-xl mx-auto text-center text-sm sm:text-base text-[#8D8D99] leading-7 mb-12">
          Create your Raava account or sign in with your email or access key.
        </p>

        <div className="relative rounded-[26px] p-px bg-[linear-gradient(135deg,rgba(53,175,192,0.55),rgba(35,93,188,0.4)_48%,rgba(44,169,255,0.27))] shadow-[0_30px_110px_rgba(0,0,0,0.62),0_0_70px_rgba(43,159,180,0.035)]" data-testid="access-terminal">
          <div className="relative rounded-[25px] overflow-hidden bg-[#08080B]">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_12%_0%,rgba(43,159,180,0.09),transparent_30%),radial-gradient(circle_at_92%_100%,rgba(91,70,160,0.09),transparent_34%)]" />
          <div className="relative flex items-center gap-4 px-5 sm:px-7 py-4 border-b border-[#27272E] bg-[#050507]/90">
            <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#258DFF]/65" />
            <span className="w-2 h-2 rounded-full bg-[#2B9FB4]/55" />
            <span className="w-2 h-2 rounded-full bg-[#7C5CFC]/55" />
            <span className="ml-3 text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] flex items-center gap-2">
              <Terminal className="w-3 h-3 text-[#35AFC0]" /> ACCOUNT LOGIN
            </span>
            </div>
          </div>

          <div className="relative grid grid-cols-2 gap-2 m-3 p-1.5 rounded-2xl border border-[#25252D] bg-[#050507]/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.025)]">
            {[
              { id: "register", label: "Create Account", icon: UserPlus },
              { id: "login", label: "Sign In", icon: LogIn },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => { setMode(t.id); setIssuedKey(null); setRecoveryMessage(""); setVerificationEmail(""); }}
                className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border text-[10px] sm:text-xs uppercase tracking-[0.18em] transition-all duration-300 ${
                  mode === t.id || (t.id === "login" && mode === "forgot") ? "text-[#BCEFF5] border-[#2B9FB4]/45 bg-gradient-to-r from-[#0A2931] to-[#151D34] shadow-[0_0_20px_rgba(43,159,180,0.08)]" : "border-transparent text-[#A1A1AA] hover:text-[#F4F4F5] hover:bg-white/[0.03]"
                }`}
                data-testid={`access-tab-${t.id}`}
              >
                <t.icon className="w-4 h-4" strokeWidth={1.8} /> {t.label}
              </button>
            ))}
          </div>

          <div className="relative p-6 sm:p-10">
            <AnimatePresence mode="wait">
              {mode === "register" ? (
                <motion.form
                  key="register"
                  name="raava-register"
                  method="post"
                  autoComplete="on"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.25 }}
                  onSubmit={handleRegister}
                  className="space-y-5"
                  data-testid="register-form"
                >
                  <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="raava-register-name" className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-3">Name</label>
                    <input
                      id="raava-register-name"
                      name="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      placeholder="Ada Lovelace"
                      autoComplete="name"
                      className="w-full rounded-xl bg-[#030303] border border-[#292930] px-4 py-3.5 text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)] transition-all duration-300"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="raava-register-email" className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-3">Email</label>
                    <input
                      id="raava-register-email"
                      name="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="ada@raava.dev"
                      autoComplete="email"
                      className="w-full rounded-xl bg-[#030303] border border-[#292930] px-4 py-3.5 text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)] transition-all duration-300"
                      data-testid="register-email-input"
                    />
                  </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="raava-register-password" className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-3">Password</label>
                    <input
                      id="raava-register-password"
                      name="new-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={10}
                      maxLength={128}
                      autoComplete="new-password"
                      placeholder="At least 10 characters"
                      className="w-full rounded-xl bg-[#030303] border border-[#292930] px-4 py-3.5 text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)] transition-all duration-300"
                      data-testid="register-password-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="raava-register-password-confirm" className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-3">Confirm Password</label>
                    <input
                      id="raava-register-password-confirm"
                      name="new-password-confirmation"
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      required
                      minLength={10}
                      maxLength={128}
                      autoComplete="new-password"
                      placeholder="Repeat your password"
                      className="w-full rounded-xl bg-[#030303] border border-[#292930] px-4 py-3.5 text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)] transition-all duration-300"
                      data-testid="register-password-confirm-input"
                    />
                  </div>
                  </div>
                  <div className={`rounded-2xl border p-4 transition ${termsAccepted ? "border-[#35C982]/35 bg-[#35C982]/[0.05]" : "border-[#2A2A32] bg-[#050508]"}`} data-testid="registration-terms-panel">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-start gap-3">
                        {termsAccepted ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#35C982]" /> : <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-[#58B7C5]" />}
                        <div>
                          <p className="text-xs font-semibold text-[#E8E8EC]">{termsAccepted ? "Terms accepted" : "Review required"}</p>
                          <p className="mt-1 text-[10px] leading-5 text-[#858590]">Read the complete Terms and scroll to the end before registration.</p>
                        </div>
                      </div>
                      <button type="button" onClick={() => { setTermsReachedEnd(false); setTermsOpen(true); }} className="shrink-0 rounded-xl border border-[#388FA4]/45 px-4 py-3 text-[9px] uppercase tracking-[0.18em] text-[#83CFD9] hover:bg-[#2B9FB4]/10">
                        {termsAccepted ? "Read again" : "Read Terms"}
                      </button>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 border-t border-[#23232A] pt-3 text-[9px] uppercase tracking-[0.16em]">
                      <a href="/terms" target="_blank" rel="noreferrer" className="text-[#6CBECB] hover:text-white">Terms</a>
                      <a href="/software-license" target="_blank" rel="noreferrer" className="text-[#8E7FD0] hover:text-white">Software License</a>
                      <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#A47E91] hover:text-white">Privacy</a>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={busy || !termsAccepted}
                    className="w-full rounded-xl py-4 border border-[#388FA4]/55 bg-gradient-to-r from-[#0E5261] via-[#145A70] to-[#303F82] text-[#F3FCFF] text-xs font-semibold uppercase tracking-[0.22em] shadow-[0_12px_32px_rgba(7,44,58,0.24)] hover:brightness-115 disabled:opacity-40 transition-all duration-300"
                    data-testid="register-submit-button"
                  >
                    {busy ? "Creating account…" : "Create Account"}
                  </button>

                  <AnimatePresence>
                    {issuedKey && (
                      <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="mt-4 rounded-2xl border border-[#00FF66]/40 bg-[#00FF66]/5 p-6"
                        data-testid="issued-key-panel"
                      >
                        <p className="text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-4">
                          Your Access Key — shown only once
                        </p>
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <span className="text-lg sm:text-xl text-[#00FF66] neon-glow tracking-[0.15em]" data-testid="issued-key-value">
                            {issuedKey}
                          </span>
                          <button
                            type="button"
                            onClick={copyKey}
                            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#00FF66] transition-colors duration-300"
                            data-testid="copy-key-button"
                          >
                            <Copy className="w-3.5 h-3.5" /> Copy
                          </button>
                        </div>
                        <p className="mt-4 text-[10px] text-[#FF3366]/80 tracking-wide">
                          Store this key securely. It cannot be recovered.
                        </p>
                        <p className="mt-3 text-[10px] text-[#58B7C5]/90 tracking-wide" data-testid="verification-notice">
                          Your key will activate after you verify your email. We sent a verification link to {email}.
                        </p>
                        <button
                          type="button"
                          onClick={() => resendVerification(email)}
                          disabled={resendBusy || resendCooldown > 0}
                          className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#2B9FB4]/35 px-4 text-[10px] uppercase tracking-[0.15em] text-[#83CFD9] transition hover:bg-[#2B9FB4]/10 disabled:cursor-not-allowed disabled:opacity-45"
                          data-testid="registration-resend-verification-button"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${resendBusy ? "animate-spin" : ""}`} />
                          {resendCooldown > 0 ? `Send again in ${resendCooldown}s` : "Resend verification email"}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.form>
              ) : mode === "forgot" ? (
                <motion.form
                  key="forgot"
                  name="raava-password-recovery"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                  onSubmit={handlePasswordResetRequest}
                  className="mx-auto max-w-xl space-y-6"
                  data-testid="password-reset-request-form"
                >
                  <button
                    type="button"
                    onClick={() => { setMode("login"); setRecoveryMessage(""); }}
                    className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#292930] px-4 text-[10px] uppercase tracking-[0.16em] text-[#A1A1AA] transition hover:border-[#2B9FB4]/50 hover:text-[#9DE7F0]"
                    data-testid="password-reset-back-button"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to Sign In
                  </button>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#58B7C5]">Account recovery</p>
                    <h3 className="mt-3 font-display text-2xl font-bold text-[#F4F4F5]">Reset your password.</h3>
                    <p className="mt-3 text-sm leading-6 text-[#90909C]">Enter the email used for your Raava account. If it matches an email-login account, we will send a 30-minute reset link.</p>
                  </div>
                  <div>
                    <label htmlFor="raava-recovery-email" className="mb-3 block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4A4A55]" strokeWidth={1.5} />
                      <input
                        id="raava-recovery-email"
                        name="email"
                        type="email"
                        value={recoveryEmail}
                        onChange={(event) => setRecoveryEmail(event.target.value)}
                        required
                        autoComplete="email"
                        placeholder="name@example.com"
                        className="w-full rounded-xl border border-[#292930] bg-[#030303] py-3.5 pl-11 pr-4 text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] transition-all duration-300 focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)]"
                        data-testid="password-reset-request-email-input"
                      />
                    </div>
                  </div>
                  {recoveryMessage && (
                    <div className="rounded-2xl border border-[#35C982]/30 bg-[#35C982]/[0.055] p-4 text-sm leading-6 text-[#8BE3B7]" role="status" aria-live="polite" data-testid="password-reset-request-success">
                      {recoveryMessage}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={busy || !recoveryEmail.includes("@")}
                    className="w-full rounded-xl border border-[#388FA4]/55 bg-gradient-to-r from-[#102D3A] via-[#174B5C] to-[#322B67] py-4 text-xs font-semibold uppercase tracking-[0.2em] text-[#DDF8FC] shadow-[0_12px_32px_rgba(7,35,48,0.2)] transition hover:brightness-120 disabled:cursor-not-allowed disabled:opacity-35"
                    data-testid="password-reset-request-submit-button"
                  >
                    {busy ? "Sending…" : "Send reset link"}
                  </button>
                  <p className="text-center text-[11px] leading-5 text-[#686874]">Access Keys cannot be recovered by email. If you lost a key-only login, contact Support.</p>
                </motion.form>
              ) : (
                <motion.form
                  key="login"
                  name="raava-login"
                  method="post"
                  autoComplete="on"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                  onSubmit={handleLogin}
                  className="space-y-6"
                  data-testid="login-form"
                >
                  <div className="grid grid-cols-2 gap-2" data-testid="login-kind-switch">
                    <button type="button" onClick={() => { setLoginKind("email"); setIdentifier(""); setShowAccessKey(false); }} className={`rounded-xl px-3 py-3.5 border text-[10px] uppercase tracking-[0.18em] transition-all ${loginKind === "email" ? "border-[#00FF66]/60 bg-[#00FF66]/5 text-[#00FF66]" : "border-[#292930] text-[#A1A1AA] hover:border-[#555560]"}`}>Email + Password</button>
                    <button type="button" onClick={() => { setLoginKind("key"); setIdentifier(""); setLoginPassword(""); setShowAccessKey(false); }} className={`rounded-xl px-3 py-3.5 border text-[10px] uppercase tracking-[0.18em] transition-all ${loginKind === "key" ? "border-[#2B9FB4]/55 bg-[#2B9FB4]/[0.07] text-[#72C9D3]" : "border-[#292930] text-[#A1A1AA] hover:border-[#555560]"}`}>Access Key</button>
                  </div>
                  <div>
                    <label htmlFor="raava-login-identifier" className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA] mb-3">{loginKind === "key" ? "Access Key" : "Email"}</label>
                    <div className="relative">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#333]" strokeWidth={1.5} />
                      <input
                        id="raava-login-identifier"
                        name={loginKind === "key" ? "raava_access_key" : "username"}
                        type={loginKind === "key" && !showAccessKey ? "password" : loginKind === "email" ? "email" : "text"}
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        required
                        maxLength={64}
                        placeholder={loginKind === "key" ? "RAAVA-XXXX-XXXX-XXXX-XXXX" : "name@example.com"}
                        autoComplete={loginKind === "key" ? "off" : "username webauthn"}
                        spellCheck={false}
                        autoCapitalize="none"
                        className={`w-full rounded-xl bg-[#030303] border border-[#292930] pl-11 ${loginKind === "key" ? "pr-12" : "pr-4"} py-3.5 font-mono text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)] transition-all duration-300`}
                        data-testid="login-identifier-input"
                      />
                      {loginKind === "key" && (
                        <button
                          type="button"
                          onClick={() => setShowAccessKey((visible) => !visible)}
                          className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-[#72727D] transition hover:bg-white/[0.05] hover:text-[#BCEFF5]"
                          aria-label={showAccessKey ? "Hide Access Key" : "Show complete Access Key"}
                        >
                          {showAccessKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                  {loginKind === "email" && (
                    <div>
                      <div className="mb-3 flex items-center justify-between gap-4">
                        <label htmlFor="raava-login-password" className="block text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">Password</label>
                        <button type="button" onClick={openPasswordRecovery} className="min-h-8 text-[10px] font-medium text-[#68C5D1] transition hover:text-[#BCEFF5]" data-testid="login-forgot-password-link">
                          Forgot password?
                        </button>
                      </div>
                      <input
                        id="raava-login-password"
                        name="password"
                        type="password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        minLength={10}
                        maxLength={128}
                        autoComplete="current-password"
                        placeholder="Your password"
                        className="w-full rounded-xl bg-[#030303] border border-[#292930] px-4 py-3.5 text-sm text-[#F4F4F5] placeholder:text-[#4A4A55] focus:border-[#2B9FB4]/70 focus:outline-none focus:shadow-[0_0_0_3px_rgba(43,159,180,0.07)] transition-all duration-300"
                        data-testid="login-password-input"
                      />
                    </div>
                  )}
                  {loginKind === "key" && (
                    <p className="text-[10px] text-[#58B7C5]/80 tracking-wide" data-testid="key-password-hint">
                      The complete key is kept unchanged and normalized only when you submit it.
                    </p>
                  )}
                  {verificationEmail && (
                    <div className="rounded-2xl border border-[#FFB020]/30 bg-[#FFB020]/[0.055] p-4" role="status" aria-live="polite" data-testid="login-verification-required">
                      <p className="text-sm font-semibold text-[#FFD27A]">Email verification required</p>
                      <p className="mt-1 text-xs leading-5 text-[#A9A09A]">Your login details are correct, but the account is not active yet.</p>
                      <button
                        type="button"
                        onClick={() => resendVerification(verificationEmail)}
                        disabled={resendBusy || resendCooldown > 0}
                        className="mt-3 inline-flex min-h-11 items-center gap-2 rounded-xl border border-[#FFB020]/30 px-4 text-[10px] uppercase tracking-[0.14em] text-[#FFD27A] transition hover:bg-[#FFB020]/10 disabled:cursor-not-allowed disabled:opacity-45"
                        data-testid="login-resend-verification-button"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${resendBusy ? "animate-spin" : ""}`} />
                        {resendCooldown > 0 ? `Send again in ${resendCooldown}s` : "Resend verification email"}
                      </button>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full rounded-xl py-4 border border-[#388FA4]/55 bg-gradient-to-r from-[#102D3A] via-[#174B5C] to-[#322B67] text-[#DDF8FC] text-xs font-semibold uppercase tracking-[0.22em] shadow-[0_12px_32px_rgba(7,35,48,0.2)] hover:brightness-120 disabled:opacity-30 disabled:hover:brightness-100 transition-all duration-300"
                    data-testid="login-submit-button"
                  >
                    {busy ? "Verifying…" : "Sign In"}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
          </div>
        </div>
      </div>
      <TermsReviewModal
        open={termsOpen}
        reachedEnd={termsReachedEnd}
        onReachedEnd={() => setTermsReachedEnd(true)}
        onClose={() => setTermsOpen(false)}
        onAccept={() => { setTermsAccepted(true); setTermsOpen(false); toast.success("Terms accepted"); }}
      />
    </section>
  );
}
